// 导入导出服务 - 支持多种格式的数据导入导出
class ImportExportService {
  constructor() {
    this.supportedFormats = {
      json: { name: 'JSON', extension: '.json', mimeType: 'application/json' },
      csv: { name: 'CSV', extension: '.csv', mimeType: 'text/csv' },
      xml: { name: 'XML', extension: '.xml', mimeType: 'application/xml' },
      zip: { name: 'ZIP', extension: '.zip', mimeType: 'application/zip' }
    }
    this.maxFileSize = 50 * 1024 * 1024 // 50MB
  }

  // 导出故事数据
  async exportStories(storyIds = [], format = 'json') {
    try {
      const stories = await this.getStoriesData(storyIds)
      const exportData = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        type: 'stories',
        data: stories
      }

      switch (format) {
        case 'json':
          return this.exportToJSON(exportData)
        case 'csv':
          return this.exportToCSV(stories)
        case 'xml':
          return this.exportToXML(exportData)
        case 'zip':
          return this.exportToZIP(exportData)
        default:
          throw new Error(`不支持的导出格式: ${format}`)
      }
    } catch (error) {
      console.error('导出故事失败:', error)
      throw error
    }
  }

  // 导出媒资数据
  async exportMedia(mediaIds = [], format = 'json') {
    try {
      const media = await this.getMediaData(mediaIds)
      const exportData = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        type: 'media',
        data: media
      }

      switch (format) {
        case 'json':
          return this.exportToJSON(exportData)
        case 'csv':
          return this.exportToCSV(media)
        case 'zip':
          return this.exportToZIP(exportData)
        default:
          throw new Error(`不支持的导出格式: ${format}`)
      }
    } catch (error) {
      console.error('导出媒资失败:', error)
      throw error
    }
  }

  // 导出完整数据
  async exportAllData(format = 'json') {
    try {
      const stories = await this.getStoriesData()
      const media = await this.getMediaData()
      const settings = this.getSettingsData()
      
      const exportData = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        type: 'full-export',
        data: {
          stories,
          media,
          settings
        }
      }

      switch (format) {
        case 'json':
          return this.exportToJSON(exportData)
        case 'zip':
          return this.exportToZIP(exportData)
        default:
          throw new Error(`不支持的导出格式: ${format}`)
      }
    } catch (error) {
      console.error('导出完整数据失败:', error)
      throw error
    }
  }

  // 导入数据
  async importData(file, options = {}) {
    try {
      this.validateFile(file)
      
      const fileContent = await this.readFile(file)
      const data = await this.parseFile(fileContent, file.type)
      
      const result = {
        success: true,
        imported: 0,
        skipped: 0,
        errors: [],
        details: []
      }

      // 根据数据类型进行导入
      if (data.type === 'stories' || data.type === 'full-export') {
        const storyResult = await this.importStories(data.data.stories || data.data, options)
        result.imported += storyResult.imported
        result.skipped += storyResult.skipped
        result.errors.push(...storyResult.errors)
        result.details.push(...storyResult.details)
      }

      if (data.type === 'media' || data.type === 'full-export') {
        const mediaResult = await this.importMedia(data.data.media || data.data, options)
        result.imported += mediaResult.imported
        result.skipped += mediaResult.skipped
        result.errors.push(...mediaResult.errors)
        result.details.push(...mediaResult.details)
      }

      if (data.type === 'full-export' && data.data.settings) {
        const settingsResult = await this.importSettings(data.data.settings, options)
        result.imported += settingsResult.imported
        result.skipped += settingsResult.skipped
        result.errors.push(...settingsResult.errors)
        result.details.push(...settingsResult.details)
      }

      return result
    } catch (error) {
      console.error('导入数据失败:', error)
      return {
        success: false,
        imported: 0,
        skipped: 0,
        errors: [error.message],
        details: []
      }
    }
  }

  // 获取故事数据
  async getStoriesData(storyIds = []) {
    const stories = []
    
    // 从localStorage获取故事
    const storiesKey = 'altverse-story-store'
    const storiesData = localStorage.getItem(storiesKey)
    if (storiesData) {
      const parsed = JSON.parse(storiesData)
      if (parsed.state && parsed.state.stories) {
        const allStories = parsed.state.stories
        const filteredStories = storyIds.length > 0 
          ? allStories.filter(story => storyIds.includes(story.id))
          : allStories
        stories.push(...filteredStories)
      }
    }

    // 从用户编辑数据获取
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('user-edited-')) {
        const storyId = key.replace('user-edited-', '')
        if (storyIds.length === 0 || storyIds.includes(storyId)) {
          try {
            const data = JSON.parse(localStorage.getItem(key))
            stories.push({
              id: storyId,
              ...data,
              source: 'user-edited'
            })
          } catch (error) {
            console.warn(`解析用户编辑数据失败: ${key}`, error)
          }
        }
      }
    }

    return stories
  }

  // 获取媒资数据
  async getMediaData(mediaIds = []) {
    const media = []
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('altverse-media-')) {
        const mediaId = key.replace('altverse-media-', '')
        if (mediaIds.length === 0 || mediaIds.includes(mediaId)) {
          try {
            const data = JSON.parse(localStorage.getItem(key))
            media.push(data)
          } catch (error) {
            console.warn(`解析媒资数据失败: ${key}`, error)
          }
        }
      }
    }

    return media
  }

  // 获取设置数据
  getSettingsData() {
    const settings = {}
    
    // 获取所有设置相关的localStorage项
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.startsWith('altverse-') || key.includes('settings'))) {
        try {
          const value = localStorage.getItem(key)
          settings[key] = JSON.parse(value)
        } catch (error) {
          settings[key] = value
        }
      }
    }

    return settings
  }

  // 导出为JSON
  exportToJSON(data) {
    const jsonString = JSON.stringify(data, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    return this.createDownload(blob, 'altverse-export.json')
  }

  // 导出为CSV
  exportToCSV(data) {
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('CSV导出需要数组数据')
    }

    const headers = Object.keys(data[0])
    const csvContent = [
      headers.join(','),
      ...data.map(item => 
        headers.map(header => {
          const value = item[header]
          return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value
        }).join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
    return this.createDownload(blob, 'altverse-export.csv')
  }

  // 导出为XML
  exportToXML(data) {
    const xmlContent = this.objectToXML(data, 'altverse-export')
    const blob = new Blob([xmlContent], { type: 'application/xml' })
    return this.createDownload(blob, 'altverse-export.xml')
  }

  // 导出为ZIP
  async exportToZIP(data) {
    // 这里需要引入JSZip库
    // 暂时返回JSON格式
    console.warn('ZIP导出功能需要JSZip库支持')
    return this.exportToJSON(data)
  }

  // 导入故事
  async importStories(stories, options = {}) {
    const result = {
      imported: 0,
      skipped: 0,
      errors: [],
      details: []
    }

    for (const story of stories) {
      try {
        if (!story.id) {
          result.errors.push('故事缺少ID')
          continue
        }

        // 检查是否已存在
        const existingKey = `altverse-story-${story.id}`
        if (localStorage.getItem(existingKey) && !options.overwrite) {
          result.skipped++
          result.details.push(`跳过已存在的故事: ${story.title}`)
          continue
        }

        // 保存故事
        localStorage.setItem(existingKey, JSON.stringify(story))
        
        // 更新故事列表
        this.updateStoriesList(story)
        
        result.imported++
        result.details.push(`导入故事: ${story.title}`)
      } catch (error) {
        result.errors.push(`导入故事失败: ${story.title} - ${error.message}`)
      }
    }

    return result
  }

  // 导入媒资
  async importMedia(media, options = {}) {
    const result = {
      imported: 0,
      skipped: 0,
      errors: [],
      details: []
    }

    for (const item of media) {
      try {
        if (!item.id) {
          result.errors.push('媒资缺少ID')
          continue
        }

        // 检查是否已存在
        const existingKey = `altverse-media-${item.id}`
        if (localStorage.getItem(existingKey) && !options.overwrite) {
          result.skipped++
          result.details.push(`跳过已存在的媒资: ${item.name}`)
          continue
        }

        // 保存媒资
        localStorage.setItem(existingKey, JSON.stringify(item))
        
        result.imported++
        result.details.push(`导入媒资: ${item.name}`)
      } catch (error) {
        result.errors.push(`导入媒资失败: ${item.name} - ${error.message}`)
      }
    }

    return result
  }

  // 导入设置
  async importSettings(settings, options = {}) {
    const result = {
      imported: 0,
      skipped: 0,
      errors: [],
      details: []
    }

    for (const [key, value] of Object.entries(settings)) {
      try {
        if (localStorage.getItem(key) && !options.overwrite) {
          result.skipped++
          result.details.push(`跳过已存在的设置: ${key}`)
          continue
        }

        localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value))
        result.imported++
        result.details.push(`导入设置: ${key}`)
      } catch (error) {
        result.errors.push(`导入设置失败: ${key} - ${error.message}`)
      }
    }

    return result
  }

  // 更新故事列表
  updateStoriesList(story) {
    try {
      const storiesKey = 'altverse-story-store'
      const storiesData = localStorage.getItem(storiesKey)
      
      if (storiesData) {
        const parsed = JSON.parse(storiesData)
        if (parsed.state && parsed.state.stories) {
          const existingIndex = parsed.state.stories.findIndex(s => s.id === story.id)
          if (existingIndex >= 0) {
            parsed.state.stories[existingIndex] = story
          } else {
            parsed.state.stories.push(story)
          }
          localStorage.setItem(storiesKey, JSON.stringify(parsed))
        }
      }
    } catch (error) {
      console.warn('更新故事列表失败:', error)
    }
  }

  // 验证文件
  validateFile(file) {
    if (!file) {
      throw new Error('请选择要导入的文件')
    }

    if (file.size > this.maxFileSize) {
      throw new Error(`文件过大，最大支持 ${this.maxFileSize / 1024 / 1024}MB`)
    }

    const supportedTypes = Object.values(this.supportedFormats).map(f => f.mimeType)
    if (!supportedTypes.includes(file.type)) {
      throw new Error('不支持的文件格式')
    }
  }

  // 读取文件
  readFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target.result)
      reader.onerror = (e) => reject(new Error('文件读取失败'))
      reader.readAsText(file)
    })
  }

  // 解析文件
  async parseFile(content, mimeType) {
    try {
      if (mimeType === 'application/json') {
        return JSON.parse(content)
      } else if (mimeType === 'text/csv') {
        return this.parseCSV(content)
      } else if (mimeType === 'application/xml') {
        return this.parseXML(content)
      } else {
        throw new Error('不支持的文件格式')
      }
    } catch (error) {
      throw new Error(`文件解析失败: ${error.message}`)
    }
  }

  // 解析CSV
  parseCSV(content) {
    const lines = content.split('\n')
    const headers = lines[0].split(',').map(h => h.replace(/"/g, ''))
    const data = lines.slice(1).map(line => {
      const values = line.split(',')
      const item = {}
      headers.forEach((header, index) => {
        item[header] = values[index] ? values[index].replace(/"/g, '') : ''
      })
      return item
    })
    return { type: 'stories', data }
  }

  // 解析XML
  parseXML(content) {
    // 简单的XML解析，实际项目中建议使用专门的XML解析库
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(content, 'application/xml')
    return this.xmlToObject(xmlDoc.documentElement)
  }

  // XML转对象
  xmlToObject(node) {
    const result = {}
    
    if (node.nodeType === 1) { // Element node
      if (node.children.length === 0) {
        return node.textContent
      }
      
      for (const child of node.children) {
        const key = child.nodeName
        const value = this.xmlToObject(child)
        
        if (result[key]) {
          if (!Array.isArray(result[key])) {
            result[key] = [result[key]]
          }
          result[key].push(value)
        } else {
          result[key] = value
        }
      }
    }
    
    return result
  }

  // 对象转XML
  objectToXML(obj, rootName = 'root') {
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n<${rootName}>\n`
    xml += this.objectToXMLString(obj, 1)
    xml += `</${rootName}>`
    return xml
  }

  // 对象转XML字符串
  objectToXMLString(obj, indent = 0) {
    let xml = ''
    const spaces = '  '.repeat(indent)
    
    for (const [key, value] of Object.entries(obj)) {
      if (Array.isArray(value)) {
        for (const item of value) {
          xml += `${spaces}<${key}>\n`
          xml += this.objectToXMLString(item, indent + 1)
          xml += `${spaces}</${key}>\n`
        }
      } else if (typeof value === 'object' && value !== null) {
        xml += `${spaces}<${key}>\n`
        xml += this.objectToXMLString(value, indent + 1)
        xml += `${spaces}</${key}>\n`
      } else {
        xml += `${spaces}<${key}>${value}</${key}>\n`
      }
    }
    
    return xml
  }

  // 创建下载
  createDownload(blob, filename) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    return { success: true, filename }
  }

  // 获取支持的格式
  getSupportedFormats() {
    return this.supportedFormats
  }
}

// 创建单例实例
const importExportService = new ImportExportService()

export default importExportService
